import React, { useState, useEffect } from 'react';
import { X, BookOpen, User, Box, MessageCircle, Truck, CheckCircle, Clock, XCircle, Save, Star } from 'lucide-react';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  onStatusUpdate: (orderId: string, status: string) => Promise<void>;
  onRequestReview?: (orderId: string) => Promise<void>;
}

const PIPELINE_STEPS = ['pending', 'confirmed', 'shipped', 'delivered'];

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ isOpen, onClose, order, onStatusUpdate, onRequestReview }) => {
  const [internalNote, setInternalNote] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (order?.order_id) {
      const savedNote = localStorage.getItem(`order_note_${order.order_id}`);
      setInternalNote(savedNote || '');
    }
  }, [order?.order_id]);

  if (!isOpen || !order) return null;

  const handleSaveNote = () => {
    localStorage.setItem(`order_note_${order.order_id}`, internalNote);
    alert('Note saved to local storage!');
  };

  const getPhoneForWhatsApp = (phone: string | null) => {
    if (!phone) return null;
    const clean = phone.replace(/\D/g, '');
    if (clean.startsWith('0')) {
      return `94${clean.slice(1)}`;
    }
    return clean;
  };

  const waNumber = getPhoneForWhatsApp(order.buyer_phone);

  const handleStatusUpdate = async (status: string) => {
    setIsUpdating(true);
    await onStatusUpdate(order.order_id, status);
    setIsUpdating(false);
  };

  const pipelineIndex = PIPELINE_STEPS.indexOf(order.status);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-[#F1EFE8] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-[#F1EFE8] z-10 p-5 px-6 border-b border-gray-300 flex justify-between items-center rounded-t-2xl">
          <h2 className="text-xl font-bold text-teal-900 flex items-center gap-2">
            <span>Order</span> 
            <span className="text-[#085041] px-2 py-1 bg-[#085041]/10 rounded-md font-mono text-lg">
              #{order.order_id.slice(0, 8)}
            </span>
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Order Status Pipeline Visual */}
          {order.status !== 'cancelled' ? (
            <div className="bg-white p-5 rounded-xl border border-gray-200">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Box size={16} /> Order Status Pipeline
              </h3>
              <div className="relative flex items-center justify-between w-full mt-6">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-200 z-0 rounded-full"></div>
                <div 
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-[#085041] z-0 transition-all duration-500 rounded-full"
                  style={{ width: `${Math.max(0, (pipelineIndex / (PIPELINE_STEPS.length - 1)) * 100)}%` }}
                ></div>
                
                {PIPELINE_STEPS.map((step, idx) => {
                  const isCompleted = pipelineIndex > idx;
                  const isCurrent = pipelineIndex === idx;
                  let dotClass = "bg-gray-300 border-4 border-white";
                  let textClass = "text-gray-400";
                  
                  if (isCompleted) {
                    dotClass = "bg-[#085041] border-4 border-[#085041]";
                    textClass = "text-[#085041] font-medium";
                  } else if (isCurrent) {
                    dotClass = "bg-[#1D9E75] border-4 border-[#1D9E75]/30 shadow-[0_0_0_4px_rgba(29,158,117,0.2)] animate-pulse";
                    textClass = "text-[#1D9E75] font-bold";
                  }

                  return (
                    <div key={step} className="relative z-10 flex flex-col items-center gap-2">
                      <div className={`w-5 h-5 rounded-full ${dotClass} transition-colors duration-300`}></div>
                      <span className={`text-xs capitalize absolute top-6 whitespace-nowrap ${textClass}`}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="h-4"></div> {/* spacer for labels */}
            </div>
          ) : (
            <div className="bg-red-50 text-[#D85A30] p-4 rounded-xl border border-red-200 flex items-center gap-3">
              <XCircle size={24} />
              <span className="font-semibold">This order has been cancelled.</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Book Details */}
            <div className="bg-white p-5 rounded-xl border border-gray-200 flex flex-col gap-4">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <BookOpen size={16} /> Book Details
              </h3>
              <div className="flex gap-4 items-start">
                {order.cover ? (
                  <img src={order.cover} alt="Cover" className="w-[60px] h-[85px] object-cover rounded shadow-sm" />
                ) : (
                  <div className="w-[60px] h-[85px] bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                    <BookOpen size={24} className="text-gray-400" />
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <h4 className="font-bold text-lg text-gray-900 leading-tight">{order.listing_title}</h4>
                  <div className="text-teal-700 font-semibold bg-teal-50 px-2 py-0.5 rounded w-fit mt-1">
                    Rs. {order.price_lkr.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">Condition:</span> {order.condition}
                  </div>
                </div>
              </div>
            </div>

            {/* Buyer Details */}
            <div className="bg-white p-5 rounded-xl border border-gray-200 flex flex-col gap-4">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <User size={16} /> Buyer Details
              </h3>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-16">Name:</span>
                  <span className="font-medium text-gray-900">{order.buyer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-16">City:</span>
                  <span className="font-medium text-gray-900">{order.buyer_city}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 w-16">Phone:</span>
                  <span className="font-medium text-gray-900">{order.buyer_phone || 'N/A'}</span>
                </div>
              </div>
              
              {waNumber && (
                <a 
                  href={`https://wa.me/${waNumber}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b958] text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  <MessageCircle size={18} /> WhatsApp Buyer
                </a>
              )}
            </div>
          </div>

          {/* Update Status Actions */}
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <div className="bg-white p-5 rounded-xl border border-gray-200">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Clock size={16} /> Update Status
              </h3>
              <div className="flex flex-wrap gap-3">
                {order.status === 'pending' && (
                  <button 
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate('confirmed')}
                    className="flex items-center gap-2 bg-[#085041] hover:bg-teal-900 text-white py-2 px-5 rounded-lg font-medium transition-colors"
                  >
                    <CheckCircle size={18} /> Confirm Order
                  </button>
                )}
                {order.status === 'confirmed' && (
                  <button 
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate('shipped')}
                    className="flex items-center gap-2 bg-[#1D9E75] hover:bg-emerald-700 text-white py-2 px-5 rounded-lg font-medium transition-colors"
                  >
                    <Truck size={18} /> Mark Shipped
                  </button>
                )}
                {order.status === 'shipped' && (
                  <button 
                    disabled={isUpdating}
                    onClick={() => handleStatusUpdate('delivered')}
                    className="flex items-center gap-2 bg-[#085041] hover:bg-teal-900 text-white py-2 px-5 rounded-lg font-medium transition-colors"
                  >
                    <Box size={18} /> Mark Delivered
                  </button>
                )}
                
                <button 
                  disabled={isUpdating}
                  onClick={() => {
                    if (window.confirm("Are you sure you want to cancel this order? This cannot be undone.")) {
                      handleStatusUpdate('cancelled');
                    }
                  }}
                  className="flex items-center gap-2 border border-[#D85A30] text-[#D85A30] hover:bg-red-50 py-2 px-5 rounded-lg font-medium transition-colors ml-auto"
                >
                  <XCircle size={18} /> Cancel Order
                </button>
              </div>
            </div>
          )}

          {order.status === 'delivered' && onRequestReview && (
             <div className="bg-white p-5 rounded-xl border border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Order Complete</h3>
                  <p className="text-sm text-gray-500">Ask the buyer to leave a review for your shop.</p>
                </div>
                <button 
                  disabled={isUpdating}
                  onClick={async () => {
                    setIsUpdating(true);
                    await onRequestReview(order.order_id);
                    setIsUpdating(false);
                  }}
                  className="flex items-center gap-2 bg-[#BA7517] hover:bg-amber-700 text-white py-2 px-5 rounded-lg font-medium transition-colors"
                >
                  <Star size={18} /> Request Review
                </button>
             </div>
          )}

          {/* Internal Note */}
          <div className="bg-white p-5 rounded-xl border border-gray-200">
            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Save size={16} /> Internal Note <span className="normal-case font-normal text-gray-400">(Seller Only)</span>
            </h3>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Add note about this order..."
              className="w-full h-24 border border-gray-300 rounded-lg p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
            <div className="flex justify-end mt-3">
              <button 
                onClick={handleSaveNote}
                className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors shadow-sm"
              >
                Save Note
              </button>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};
